/**
 * Optimized API for Explore page - uses lightweight cards endpoint
 */

import { API_BASE_URL, getAuthHeader, getResearchDetailsWithFullReport } from './api'

// Define the lightweight card type
export interface ResearchCard {
  id: number
  title: string
  summary?: string | null
  category: string
  created_at: string
  updated_at: string
  author_name: string | null
  author_avatar?: string | null
  author_email?: string | null
  image_url: string | null
  save_count: number
  like_count: number
  liked_by_user?: boolean
  original_query?: string | null
  is_intelligence_report?: boolean
  intelligence_report_id?: string | null
  podcast_status?: 'missing' | 'queued' | 'processing' | 'ready' | 'failed'
  podcast_audio_ready?: boolean
  podcast_duration_seconds?: number | null
  podcast_error_message?: string | null
}

export interface ExploreCardsResponse {
  cards: ResearchCard[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

/**
 * Fetch explore cards - lightweight data for card display
 * Full report content is NOT included - must be fetched separately when needed
 */
export async function fetchExploreCards(
  limit: number = 50,
  offset: number = 0,
  search?: string,
  category?: string
): Promise<ExploreCardsResponse> {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    })
    
    if (search) params.append('search', search)
    if (category) params.append('category', category)
    
    const response = await fetch(`${API_BASE_URL}/research/explore/cards?${params}`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch explore cards: ${response.status}`)
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching explore cards:', error)
    throw error
  }
}

/**
 * Fetch full research details (including full summary but not report text)
 */
export async function fetchResearchDetails(researchId: number): Promise<any> {
  // Use the optimized version from api.ts that handles caching and hydration efficiently
  return getResearchDetailsWithFullReport(researchId)
}

/**
 * Fetch full report text (lazy-loaded when needed)
 * @deprecated Use getResearchDetailsWithFullReport instead which handles this internally
 */
export async function fetchResearchReport(researchId: number): Promise<string> {
  // Reuse the logic from getResearchDetailsWithFullReport to leverage caching
  const details = await getResearchDetailsWithFullReport(researchId)
  return details?.results?.report_text || ''
}
